/*
 * Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of this
 * software and associated documentation files (the "Software"), to deal in the Software
 * without restriction, including without limitation the rights to use, copy, modify,
 * merge, publish, distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
 * INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
 * PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
 * OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

import { HashLocationStrategy, LocationStrategy } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuthConfigurationService } from '@core/auth/auth-configuration.service';
import { AuthModule, OpenIdConfiguration, StsConfigLoader, LogLevel, StsConfigHttpLoader } from 'angular-auth-oidc-client';
import { defer, of } from 'rxjs';
import { STORAGE_KEYS, OIDC_CONFIG, ROUTE_PATHS } from '@core/configs/app.constants';
// Import 3rd party components
import { AlertModule } from 'ngx-bootstrap/alert';
// RECOMMENDED
import { ToastrModule } from 'ngx-toastr';
import { AppComponent } from './app.component';
// Import routing module
import { AppRoutingModule } from './app.routing.module';
import { httpInterceptorProviders } from './core/api/interceptors';
import { HttpClientModule } from '@angular/common/http';
import { CoreModule } from './core/core.module';
import { PATHS } from './core/services';
import { TOASTR_CONFIG } from './core/services/notification.service';
import { AuthenticatedLayoutModule, UnauthenticatedLayoutModule } from './layouts';
// Import containers
import { SharedModule } from './shared/shared.module';
import { MatMenuModule } from '@angular/material/menu';
import { APOLLO_PROVIDER } from './core/api/apollo.config';

export function authConfigureLoader(): StsConfigLoader {
  return new StsConfigHttpLoader(
    defer(() => {
      const appClientId = localStorage.getItem(STORAGE_KEYS.APP_CLIENT_ID);
      const userPoolId  = localStorage.getItem(STORAGE_KEYS.USER_POOL_ID);

      if (!userPoolId || !appClientId) {
        return of([] as OpenIdConfiguration[]);
      }

      const region = userPoolId.split('_')[0];
      const config: OpenIdConfiguration = {
        configId:                           appClientId,
        authority:                          `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`,
        redirectUrl:                        `${window.location.origin}/`,
        clientId:                           appClientId,
        responseType:                       OIDC_CONFIG.RESPONSE_TYPE,
        scope:                              OIDC_CONFIG.SCOPE,
        postLogoutRedirectUri:              `${window.location.origin}${ROUTE_PATHS.SIGNOUT}`,
        silentRenew:                        true,
        postLoginRoute:                     '',
        forbiddenRoute:                     ROUTE_PATHS.FORBIDDEN,
        unauthorizedRoute:                  PATHS.LOGIN.redirectPath(),
        logLevel:                           LogLevel.Warn,
        maxIdTokenIatOffsetAllowedInSeconds: OIDC_CONFIG.MAX_ID_TOKEN_IAT_OFFSET_SECS,
        historyCleanupOff:                  true,
        useRefreshToken:                    true,
        autoUserInfo:                       false,
        allowUnsafeReuseRefreshToken:       true,
      };

      return of([config]);
    })
  );
}


@NgModule({
  imports: [
    BrowserModule,
    AppRoutingModule,
    AlertModule.forRoot(),
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: authConfigureLoader,
      }
    }),
    BrowserAnimationsModule,
    CoreModule,
    HttpClientModule,
    SharedModule,
    ToastrModule.forRoot(
      TOASTR_CONFIG
    ),
    AuthenticatedLayoutModule,
    UnauthenticatedLayoutModule,
    MatMenuModule,
  ],
  declarations: [
    AppComponent
  ],
  providers: [
    HttpClient,
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
    },
    httpInterceptorProviders,
    AuthConfigurationService,
    APOLLO_PROVIDER
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
